/**
 * @fileoverview CMS Merge: [vdx-merge-here] (primary) and [vdx-merge-this] (secondary) pair by
 * matching attribute value; items from each vdx-merge-this are merged into the matching vdx-merge-here.
 * No IDs required. Empty value pairs for single merge on page. Multiple vdx-merge-this with same
 * key all merge into the same primary.
 */

import { logWarn } from './errors.js';
import { reapplyFilters } from './filters.js';

const MODULE = 'cmsmerge';

/** Selector for primary (destination) collection wrapper. */
const SELECTOR_MERGE_HERE = '[vdx-merge-here]';

/** Selector for secondary (source) collection wrapper. */
const SELECTOR_MERGE_THIS = '[vdx-merge-this]';

/** Webflow class for the items container (preferred). */
const CLASS_DYN_ITEMS = 'w-dyn-items';

/** Webflow class for the list container (fallback). */
const CLASS_DYN_LIST = 'w-dyn-list';

/** Webflow class for CMS list items. */
const CLASS_DYN_ITEM = 'w-dyn-item';

/**
 * Gets the attribute value as key; empty or missing becomes "".
 * @param {Element} el
 * @param {string} attr
 * @returns {string}
 */
function getKey(el, attr) {
    const v = el.getAttribute(attr);
    return v != null ? String(v).trim() : '';
}

/**
 * Finds the container for .w-dyn-item elements within or at wrapper.
 * Prefers .w-dyn-items, falls back to .w-dyn-list.
 * @param {Element} wrapper - Primary or secondary collection wrapper.
 * @returns {Element | null} The items container, or null if not found.
 */
function findListElement(wrapper) {
    if (!wrapper) return null;
    const items = wrapper.classList.contains(CLASS_DYN_ITEMS)
        ? wrapper
        : wrapper.querySelector(`.${CLASS_DYN_ITEMS}`);
    if (items) return items;
    const list = wrapper.classList.contains(CLASS_DYN_LIST)
        ? wrapper
        : wrapper.querySelector(`.${CLASS_DYN_LIST}`);
    if (list) return list;
    const hasItem = wrapper.querySelector(`.${CLASS_DYN_ITEM}`);
    return hasItem ? wrapper : null;
}

/**
 * Merges items from secondary list into primary list (append).
 * @param {Element} primaryWrapper - Element with vdx-merge-here.
 * @param {Element} secondaryWrapper - Element with vdx-merge-this.
 */
function performMerge(primaryWrapper, secondaryWrapper) {
    const primaryList = findListElement(primaryWrapper);
    const secondaryList = findListElement(secondaryWrapper);

    if (!primaryList) {
        logWarn('vdx-merge-here: no .w-dyn-items or .w-dyn-list or .w-dyn-item found inside primary wrapper.', {
            module: MODULE,
            element: primaryWrapper,
        });
        return;
    }
    if (!secondaryList) {
        logWarn('vdx-merge-this: no .w-dyn-items or .w-dyn-list or .w-dyn-item found inside secondary wrapper.', {
            module: MODULE,
            element: secondaryWrapper,
        });
        return;
    }

    const secondaryItems = Array.from(secondaryList.querySelectorAll(`.${CLASS_DYN_ITEM}`));
    if (secondaryItems.length === 0) return;

    secondaryItems.forEach((item) => primaryList.appendChild(item));
}

/** Ensures we only register the Finsweet listener once. */
let finsweetRegistered = false;

/**
 * Registers with Finsweet CMS Load to re-run merge when items are rendered (e.g. pagination).
 */
function registerFinsweetListener() {
    if (finsweetRegistered || typeof window === 'undefined') return;
    finsweetRegistered = true;
    window.fsAttributes = window.fsAttributes || [];
    window.fsAttributes.push(['cmsload', (listInstances) => {
        if (!listInstances || listInstances.length === 0) return;
        listInstances.forEach((instance) => {
            if (instance && typeof instance.on === 'function') {
                instance.on('renderitems', () => {
                    setTimeout(cmsMerge, 0);
                });
            }
        });
    }]);
}

/**
 * Runs the merge: pairs vdx-merge-here with vdx-merge-this by matching key, then merges.
 */
export function cmsMerge() {
    if (typeof document === 'undefined') return;

    const primaries = document.querySelectorAll(SELECTOR_MERGE_HERE);
    const secondaries = document.querySelectorAll(SELECTOR_MERGE_THIS);

    if (!primaries.length && !secondaries.length) return;

    if (!secondaries.length) {
        if (primaries.length) {
            logWarn('vdx-merge-here: no [vdx-merge-this] found. Add vdx-merge-this with matching value.', {
                module: MODULE,
                element: primaries[0],
            });
        }
        registerFinsweetListener();
        return;
    }

    /** @type {Map<string, Element>} key -> primary element (first wins for duplicates) */
    const primaryByKey = new Map();
    primaries.forEach((el) => {
        const key = getKey(el, 'vdx-merge-here');
        if (!primaryByKey.has(key)) primaryByKey.set(key, el);
    });

    secondaries.forEach((secondaryWrapper) => {
        const key = getKey(secondaryWrapper, 'vdx-merge-this');
        const primaryWrapper = primaryByKey.get(key);

        if (!primaryWrapper) {
            logWarn(
                `vdx-merge-this="${key || '(empty)'}": no matching [vdx-merge-here]. Add vdx-merge-here="${key || '(empty)'}" on the target list.`,
                { module: MODULE, element: secondaryWrapper }
            );
            return;
        }

        if (primaryWrapper === secondaryWrapper) {
            logWarn('vdx-merge-here and vdx-merge-this cannot be on the same element.', {
                module: MODULE,
                element: secondaryWrapper,
            });
            return;
        }

        performMerge(primaryWrapper, secondaryWrapper);
    });

    reapplyFilters();
    registerFinsweetListener();
}
